import React, { useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Dialog from '../../../../common/components/ConfirmationDialog/Dialog';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useUpdateQuickContact } from '../../../../services/quick-contacts';
import { AddressResponse } from '../../../Restaurants/types';
import { AddEditQuickContactModalBody } from '../../common/AddEditQuickContactModalBody/AddEditQuickContactModalBody';
import { QuickContactModalMode } from '../../common/AddEditQuickContactModalBody/model/enums/QuickContactModalMode';
import { useQuickContactsFilterContext } from '../../context/QuickContactsFilterContext/hooks/useQuickContactsFilterContext';
import { QuickContactResponse } from '../../model/quick-contact-response';
import { CreateNewContactForm } from '../AddNewQuickContact/components/AddNewContactsModal/form/create-new-contact-form';
import { useAddQuickContactsFormValidation } from '../AddNewQuickContact/components/AddNewContactsModal/form/use-add-quick-contacts-form-validation';

interface EditQuickContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  quickContact: QuickContactResponse;
}

const mapAddressToForm = (address: AddressResponse | null | undefined) => {
  return {
    street: address?.street ?? '',
    streetNumber: address?.streetNumber ?? '',
    postalCode: address?.postalCode ?? '',
    country: address?.city?.country,
    city: address?.city ?? undefined,
  } as Pick<CreateNewContactForm, 'street' | 'streetNumber' | 'postalCode' | 'country' | 'city'>;
};

export const EditQuickContactModal = ({ isOpen, onClose, quickContact }: EditQuickContactModalProps) => {
  const translations = useTranslations();
  const confirmCancelRef = useRef(null);
  const validationSchema = useAddQuickContactsFormValidation();
  const { quickContactsState } = useQuickContactsFilterContext();
  const { mutate: updateQuickContact, isLoading } = useUpdateQuickContact();

  const defaultValues: CreateNewContactForm = useMemo(() => {
    return {
      type: quickContact.type,
      nickname: quickContact.quickContactName,
      selectedRestaurants: [],
      ...mapAddressToForm(quickContact.address),
    };
  }, [quickContact]);

  const methods = useForm<CreateNewContactForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const handleClose = () => {
    methods.reset(defaultValues);
    onClose();
  };

  const handleSubmit = (form: CreateNewContactForm) => {
    if (!quickContactsState.selectedFnbGroupId) return;

    const address = {
      street: form.street ?? null,
      streetNumber: form.streetNumber ?? null,
      postalCode: form.postalCode ?? null,
      cityId: form.city?.id ?? null,
    };

    updateQuickContact(
      {
        quickContactId: quickContact.id,
        updateRequest: {
          id: quickContact.id,
          type: quickContact.type,
          nickname: form.nickname || '',
          address,
        },
      },
      {
        onSuccess: () => handleClose(),
      },
    );
  };

  return (
    <Dialog
      leastDestructiveRef={confirmCancelRef}
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      dialogHeader={translations('quick_contacts_edit_contact_modal_header')}
      dialogFooter={<></>}
    >
      <FormProvider {...methods}>
        <AddEditQuickContactModalBody onSubmit={handleSubmit} isLoading={isLoading} mode={QuickContactModalMode.EDIT} />
      </FormProvider>
    </Dialog>
  );
};
